import React from "react";
import { Trans, useTranslation } from "react-i18next";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";
import { LOG } from "utils";
import { useNotifications } from "features/notifications/hooks";
import {
  isAuthLapseError,
  TOKEN_EXPIRED_MESSAGE,
  EXPIRED_TOKEN_NOTIFICATION_ID,
} from "utils/error-message-utils";
import { IApiIndicatorProps } from "./definitions";
import { getErrorMessage } from "./utils";

const ApiIndicator: React.FC<IApiIndicatorProps> = (
  props: IApiIndicatorProps,
) => {
  const { t } = useTranslation();
  const title = t("components.api_indicator.title");
  const isTokenExpired = isAuthLapseError(props.error);
  const message = isTokenExpired
    ? TOKEN_EXPIRED_MESSAGE
    : getErrorMessage(props.error);
  const { addNotification } = useNotifications();

  React.useEffect(() => {
    if (props.isError) {
      if (isTokenExpired) {
        addNotification({
          id: EXPIRED_TOKEN_NOTIFICATION_ID,
          type: "error",
          content: TOKEN_EXPIRED_MESSAGE,
          dismissible: true,
        });
      } else {
        LOG.error(message);
        addNotification({
          type: "error",
          header: title,
          content: message,
          dismissible: true,
        });
      }
    }
    // addNotification excluded — underlying dispatch is stable across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isError, message, isTokenExpired, title]);

  return (
    <>
      {props.isUninitialized && (
        <StatusIndicator type="pending">
          <Trans>{"components.api_indicator.uninitialized"}</Trans>
        </StatusIndicator>
      )}
      {props.isLoading && (
        <StatusIndicator type="loading">
          <Trans>{"components.api_indicator.loading"}</Trans>
        </StatusIndicator>
      )}
      {props.isFetching && (
        <StatusIndicator type="in-progress">
          <Trans>{"components.api_indicator.refetching"}</Trans>
        </StatusIndicator>
      )}
      {props.isError && props.isAlertError && !isTokenExpired && (
        <Alert visible={true} type="error" header={title} />
      )}
      {props.isError && !props.isAlertError && !isTokenExpired && (
        <StatusIndicator type="error">{title}</StatusIndicator>
      )}
    </>
  );
};

export default ApiIndicator;
